import {
  Controller,
  type FieldPath,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { ContactsAutocomplete } from '@/components/ContactsAutocomplete';
import { useReferralContext } from '@/views/Referrals/common/ReferralContext';
import {
  findAgentTeamMembership,
  getFirstAgent,
} from '@/views/Referrals/utils';
import type { FieldValues } from '@/views/Referrals/views/NewReferral/types';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import type { ContactSuggestion } from '$contact-suggestions';

type ClientFirstNameFieldProps = {
  onUpdate?: () => void;
};

export function ClientFirstNameField({ onUpdate }: ClientFirstNameFieldProps) {
  const trackEvent = useTrackReferralsAnalytics();

  const { control, setValue } = useFormContext<FieldValues>();
  const { submitAsAgents } = useReferralContext();
  const [referralType, referringAgent, transactingAgent] = useWatch({
    control,
    name: ['referralType', 'referringAgent', 'transactingAgent'],
  });
  const firstAgent = getFirstAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });
  const selectedAgent = findAgentTeamMembership(firstAgent, submitAsAgents);
  const { id: userId, teamId } = selectedAgent ?? { id: '', teamId: '' };

  const handleClear = () => {
    setValue('client.firstName', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSelect = (contact: ContactSuggestion) => {
    trackEvent(TrackReferralsEvent.SelectClientAutocomplete, {
      contactId: contact.googleId,
    });

    const { firstName, lastName, contactResponse } = contact;

    const mappedFields: Partial<
      Record<FieldPath<FieldValues['client']>, string | null | undefined>
    > = {
      firstName,
      lastName,
      email: contactResponse?.email,
      cellPhone: contactResponse?.cellPhone,
      street1: contactResponse?.street1,
      street2: contactResponse?.street2,
      city: contactResponse?.city,
      state: contactResponse?.state,
      zip: contactResponse?.zip,
    };

    for (const [fieldName, value] of Object.entries(mappedFields)) {
      setValue(`client.${fieldName}` as FieldPath<FieldValues>, value ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    onUpdate?.();
  };

  return (
    <Controller
      control={control}
      name="client.firstName"
      render={({
        field: { onChange, onBlur, value },
        fieldState: { invalid, error },
      }) => (
        <ContactsAutocomplete
          userId={userId}
          teamId={teamId}
          fixedHeight
          fullWidth
          highlight={!value ? 'secondary' : undefined}
          error={invalid}
          helperText={error?.message}
          label="First Name"
          required
          inputValue={value}
          onChange={onChange}
          onBlur={(e) => {
            onBlur();
            onUpdate?.();

            trackEvent(TrackReferralsEvent.EditClientField, {
              value: e.target.value,
              eventTarget: 'client.firstName',
            });
          }}
          onClear={handleClear}
          includeSideContacts={false}
          includeTeamContacts={false}
          onSelectOption={handleSelect}
        />
      )}
      rules={{ required: 'Required' }}
    />
  );
}
